import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PublicShippingInfo, PublicShippingRate } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api } from '../../lib/api';
import { formatEur, formatWeight } from '../../lib/format';
import { useStoreInfo } from '../../lib/appSettings';
import { t } from '../../i18n/pt';
import styles from './EntregasDevolucoesPage.module.scss';

type LoadState = 'loading' | 'ready' | 'error';

/**
 * Public delivery and returns page.
 *
 * Portuguese consumer law requires shipping costs, delivery estimates and the
 * right of withdrawal to be available before a purchase starts — not only at
 * checkout. The cost table is read from the backoffice's own zones and rates
 * (`GET /shipping/info`) so it can never quote a price checkout does not charge.
 */
export default function EntregasDevolucoesPage(): JSX.Element {
  const storeInfo = useStoreInfo();
  const [info, setInfo] = useState<PublicShippingInfo | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<PublicShippingInfo>('/shipping/info');
        if (cancelled) return;
        setInfo(res.data);
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const contactEmail = storeInfo.email || t.footer.contactEmail;
  const zonesWithRates = info?.zones ?? [];
  const hasAnyRate = zonesWithRates.some((zone) => zone.rates.length > 0);

  return (
    <>
      <SEO
        title="Entregas e Devoluções"
        description="Custos e prazos de entrega em Portugal, e como devolver uma peça no prazo de 14 dias."
      />
      <div className="container" style={{ maxWidth: 800 }}>
        <h1>Entregas e Devoluções</h1>
        <p>
          Aqui ficas a saber quanto custa o envio, quanto tempo demora a chegar e como devolver
          uma peça se ela não for o que esperavas.
        </p>

        <h2>{t.deliveries.ratesTitle}</h2>
        {state === 'loading' ? <p>{t.deliveries.ratesLoading}</p> : null}
        {state === 'error' ? <p>{t.deliveries.ratesError}</p> : null}
        {state === 'ready' && !hasAnyRate ? <p>{t.deliveries.ratesEmpty}</p> : null}

        {state === 'ready' && hasAnyRate ? (
          <>
            {info?.freeShippingFrom ? (
              <p className={styles.freeShipping}>
                {t.deliveries.freeFrom.replace('{amount}', formatEur(info.freeShippingFrom))}
              </p>
            ) : null}

            {zonesWithRates.map((zone) => (
              <section key={zone.id} className={styles.zone}>
                <h3>{zone.name}</h3>
                {zone.description ? (
                  <p className={styles.zoneDescription}>{zone.description}</p>
                ) : null}

                {zone.rates.length === 0 ? (
                  <p className={styles.zoneDescription}>{t.deliveries.zoneNoRates}</p>
                ) : (
                  <div className={styles.tableScroll}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th scope="col">{t.deliveries.colService}</th>
                          <th scope="col">{t.deliveries.colWeight}</th>
                          <th scope="col">{t.deliveries.colPrice}</th>
                          <th scope="col">{t.deliveries.colEstimate}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mergeAdjacentRates(zone.rates).map((rate) => (
                          <tr key={rate.id}>
                            <td>{rate.name}</td>
                            <td>{formatWeightRange(rate.minWeightG, rate.maxWeightG)}</td>
                            <td>{formatEur(rate.price)}</td>
                            <td>{rate.estimatedDays ?? t.deliveries.estimateFallback}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </>
        ) : null}

        <p>
          Os custos apresentados são os cobrados no checkout, calculados a partir do peso da tua
          encomenda e do código postal de entrega. Os prazos contam-se em dias úteis a partir da
          confirmação do pagamento e são estimativas do transportador.
        </p>

        <h2>Recolha em ponto de entrega</h2>
        <p>
          Se preferires, podes escolher a recolha num dos pontos disponíveis no checkout, quando
          existirem para a tua zona. Avisamos-te por e-mail quando a encomenda estiver pronta a
          recolher e indicamos até quando a podes ir buscar.
        </p>

        <h2 id="devolucoes">Devoluções e trocas</h2>
        <p>
          Tens <strong>14 dias</strong> a contar da data em que recebes a encomenda para desistir
          da compra, sem precisares de justificar o motivo. É o direito de livre resolução previsto
          no Decreto-Lei n.º 24/2014.
        </p>
        <p>Para que a devolução seja aceite, a peça tem de vir:</p>
        <ul>
          <li>sem sinais de uso, lavagem ou alterações;</li>
          <li>com as etiquetas originais colocadas;</li>
          <li>na embalagem original, sempre que possível.</li>
        </ul>

        <h3>Como pedir</h3>
        <ol>
          <li>
            Escreve-me para <a href={`mailto:${contactEmail}`}>{contactEmail}</a> dentro dos 14
            dias, com o número da encomenda e as peças que queres devolver.
          </li>
          <li>Respondo com as instruções e a morada para onde enviar.</li>
          <li>Envia a peça no prazo de 14 dias a contar do pedido de devolução.</li>
        </ol>

        <h3>Custos e reembolso</h3>
        <p>
          Os <strong>custos do envio de retorno são por tua conta</strong>, exceto se a peça vier
          com defeito ou se te tiver sido enviada a peça errada — nesse caso o retorno é pago por
          nós.
        </p>
        <p>
          O reembolso é feito no prazo de <strong>14 dias</strong> após eu receber a peça
          devolvida, pelo mesmo meio de pagamento que usaste. Se a devolução for parcial, os portes
          da encomenda original não são reembolsados.
        </p>

        <h3>Trocas</h3>
        <p>
          Para trocar um tamanho ou uma cor, o processo é o mesmo: pedes a devolução e fazes uma
          encomenda nova. Assim garantes o tamanho que queres enquanto ainda há stock.
        </p>

        <h2>Peças com defeito</h2>
        <p>
          Se a peça chegar com defeito, tens a garantia legal de <strong>3 anos</strong> prevista no
          Decreto-Lei n.º 84/2021. Escreve-me com fotografias do defeito e o número da encomenda e
          resolvemos — reparação, substituição, redução do preço ou devolução do dinheiro.
        </p>

        <h2>Reclamações</h2>
        <p>
          Podes usar o{' '}
          <a href="https://www.livroreclamacoes.pt/" target="_blank" rel="noreferrer">
            Livro de Reclamações eletrónico
          </a>
          . As entidades de resolução alternativa de litígios a que podes recorrer estão indicadas
          nos <Link to="/termos-e-condicoes#litigios">Termos e Condições</Link>.
        </p>
      </div>
    </>
  );
}

/**
 * Collapses consecutive weight brackets that cost the same into one row.
 *
 * The backoffice needs a bracket per weight step to price a basket, but a
 * customer reading the page does not: "até 100 g — 4,60 €" says the same thing
 * as three identical rows for 0–20 g, 21–50 g and 51–100 g. Only adjacent
 * brackets with the same service name, price and estimate are merged, so a
 * genuine price change always keeps its own row.
 */
function mergeAdjacentRates(rates: PublicShippingRate[]): PublicShippingRate[] {
  return rates.reduce<PublicShippingRate[]>((merged, rate) => {
    const previous = merged[merged.length - 1];
    const continuesPrevious =
      previous &&
      previous.name === rate.name &&
      previous.price === rate.price &&
      previous.estimatedDays === rate.estimatedDays &&
      rate.minWeightG > previous.maxWeightG;

    if (continuesPrevious) {
      merged[merged.length - 1] = { ...previous, maxWeightG: rate.maxWeightG };
      return merged;
    }
    return [...merged, rate];
  }, []);
}

/** "500 g – 2 kg", or just the upper bound when the range starts at zero. */
function formatWeightRange(minWeightG: number, maxWeightG: number): string {
  const max = formatWeight(maxWeightG) ?? '—';
  const min = formatWeight(minWeightG);
  return min ? `${min} – ${max}` : `até ${max}`;
}
